/**
 * ManageContributionsSection.tsx
 *
 * Displays the user's contributed items (recipes and ingredients) in a card grid.
 * Shows loading state, contributions, or an empty state message.
 *
 * Props:
 * - loadingContributions: boolean - Whether contributions are loading.
 * - contributions: Array<Recipe | Ingredient> - List of contributed items.
 */

import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import ContributedItemCard from "./ContributedItemCard";
import { Recipe, Ingredient } from "../../types";
import { useTheme } from "@mui/material/styles";

interface Props {
  loadingContributions: boolean;
  contributions: Array<Recipe | Ingredient>;
}

/**
 * ManageContributionsSection component.
 * Renders loading, contributions grid, or empty state.
 */
const ManageContributionsSection: React.FC<Props> = ({
  loadingContributions,
  contributions,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ flex: "2 1 0px" }}>
      <Paper sx={{ p: 3, borderRadius: 0.52, boxShadow: "none" }}>
        {/* Section header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.materiom.seaweed,
              fontWeight: "bold",
            }}
          >
            Manage Your Contributions
          </Typography>
        </Box>
        {/* Loading state */}
        {loadingContributions ? (
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 5, lg: 15 },
              bgcolor: theme.palette.grey[50],
              borderRadius: 2,
              mt: 2,
              p: { xs: 4, lg: 10 },
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Loading...
            </Typography>
          </Box>
        ) : contributions.length > 0 ? (
          // Contributions grid
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2, // This sets the gap between cards
              justifyContent: "flex-start",
            }}
          >
            {contributions.map((item) => (
              <ContributedItemCard key={item._id} item={item} />
            ))}
          </Box>
        ) : (
          // Empty state
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 5, lg: 15 },
              bgcolor: theme.palette.grey[50],
              borderRadius: 2,
              mt: 2,
              p: { xs: 4, lg: 10 },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: theme.palette.materiom.seaweed,
                mb: 1,
              }}
            >
              No contributions
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", opacity: 0.5 }}
            >
              Your contributions will appear here!
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ManageContributionsSection;
